import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import captureIcon from '../../assets/capture-icon.svg';
import switchIcon from '../../assets/switch-camera.svg';
import './Capture.scss';

const Capture = () => {
    const [open, setOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string>('');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);


    const startCamera = async () => {
        try {
            setError('');

            const constraints: MediaStreamConstraints = {
                video: videoDevices.length
                    ? { deviceId: { exact: videoDevices[currentDeviceIndex]?.deviceId } }
                    : { facingMode: 'user' },
                audio: false
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

            setStream(mediaStream);
            if (videoRef.current) videoRef.current.srcObject = mediaStream;
        } catch (err) {
            console.error(err);
            setError('Unable to access camera. Please grant camera permissions.');
        }
    }


    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const handleCapture = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            if (context) {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = canvas.toDataURL('image/png');
                setCapturedImage(imageData);
                stopCamera();
            }
        }
    };

    const handleRetake = () => {
        setCapturedImage(null);
        startCamera();
        setOpen(true);
    };

    const handleConfirm = () => {
        setOpen(false);
    };

    const handleClose = () => {
        stopCamera();
        setCapturedImage(null);
        setOpen(false);
    };

    const switchCamera = () => {
        if (!videoDevices.length) return;
        stopCamera();
        setCurrentDeviceIndex(prev => (prev + 1) % videoDevices.length);
    };

    useEffect(() => {
        if (open && !capturedImage) {
            startCamera();
        }
        return () => {
            stopCamera();
        };
    }, [open, currentDeviceIndex]);

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(devices => {
            const videoInputs = devices.filter(d => d?.kind === "videoinput");
            const filteredDevices = videoInputs.filter(d => d?.label !== "OBS Virtual Camera");
            setHasMultipleCameras(filteredDevices.length > 1);
            setVideoDevices(filteredDevices);
        });
    }, []);

    return (
        <div className="capture-container">
            {capturedImage && (
                <div className="captured-image-preview">
                    <img src={capturedImage} alt="Captured" className="captured-preview-img" />
                </div>
            )}
            <Button
                className="capture-button"
                sx={{
                    backgroundColor: "#00274e"
                }}
                onClick={() => capturedImage ? handleRetake() : setOpen(true)}
                startIcon={!capturedImage &&
                    <CameraAltIcon />
                }
            >
                {capturedImage ? "Retake Photo" : "Capture Photo"}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                className="capture-dialog"
                PaperProps={{
                    sx: {
                        borderRadius: '12px'
                    }
                }}
            >
                <DialogTitle className="capture-dialog-title">
                    <span>Capture Photo</span>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="capture-dialog-content">
                    {error && (
                        <div className="capture-error">
                            {error}
                        </div>
                    )}

                    {!capturedImage ? (
                        <div className="camera-view">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="camera-video"
                            />
                            <canvas ref={canvasRef} style={{ display: 'none' }} />

                            <div className="camera-controls">
                                {hasMultipleCameras && (
                                    <IconButton
                                        onClick={switchCamera}
                                        className="switch-camera-btn"
                                        title="Switch Camera"
                                    >
                                        <img src={switchIcon} alt="Switch Camera" />
                                    </IconButton>
                                )}
                                <IconButton
                                    onClick={handleCapture}
                                    className="capture-photo-btn"
                                    disabled={!stream}
                                >
                                    <img src={captureIcon} alt="Capture" />
                                </IconButton>
                            </div>
                        </div>
                    ) : (
                        <div className="preview-view">
                            <img src={capturedImage} alt="Captured" className="captured-image" />

                            <div className="preview-controls">
                                <Button
                                    startIcon={
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 96 960 960" width="20" fill="currentColor">
                                            <path d="M451 957q-123-10-207-101t-84-216q0-77 35.5-145T295 373l43 43q-56 40-87 101t-31 131q0 104 67 182.5T451 896v61Zm58 0v-61q92-8 159-86.5T735 627q0-112-78.5-190.5T466 358h-11l60 60-43 43-133-133 133-133 43 43-60 60h11q66 0 124.5 25t102 68.5Q738 437 763.5 495T789 619q0 125-84 216T509 957Z" />
                                        </svg>
                                    }
                                    variant="outlined"
                                    onClick={handleRetake}
                                    className="retake-btn"
                                >
                                    Retake
                                </Button>
                                <Button
                                    startIcon={
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 96 960 960" width="20" fill="currentColor">
                                            <path d="M378 810 154 586l43-43 181 181 384-384 43 43-427 427Z" />
                                        </svg>
                                    }
                                    variant="contained"
                                    onClick={handleConfirm}
                                    className="confirm-btn"
                                >
                                    Confirm
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Capture;
